import { Fragment, useEffect, useRef, useState } from "react";
import { flushSync } from "react-dom";
import { useSearchParams } from "react-router-dom";
import { supabase } from "../lib/supabase";
import { basenameFromPath, studioPath } from "../lib/studio-scope";
import { arrivedWhole } from "../lib/login-code";
import {
  browserStorage,
  forgetSavedStudio,
  readSavedLogins,
  savedStudioCards,
  type SavedStudioCard,
} from "../lib/saved-logins";

/** The server's per-identifier request floor (`staff-login-link.ts` REQUEST_FLOOR_MS). */
const RESEND_AFTER_MS = 60_000;

/** One saved-studio card is a person and a studio: its list key, and what its button is filed under. */
const cardKey = (card: SavedStudioCard): string => `${card.identifier}:${card.slug}`;

/** The bold lead-in per code; the studio codes keep their historical wording. */
const STALE_TITLES: Record<string, string> = {
  whoami_unauthorized: "Session ended.",
};

/**
 * Staff sign-in (2026-08-18 — the dual-rail flow).
 *
 * One box: login email OR mobile number. `POST /api/login-link` sends the
 * sign-in EMAIL (link + 8-digit code, via Resend) and, when the row has a
 * phone, the same code over WHATSAPP (`staff_login_code` template). The
 * caller then either taps the emailed link (handled implicitly —
 * `detectSessionInUrl` parses the hash on load) or types the code here,
 * which `POST /api/login-link/verify` exchanges for a session server-side —
 * so a person who typed their PHONE never has to tell us their email.
 *
 * The response is deliberately uniform whether or not the identifier maps
 * to a staff row (non-enumeration — the server explains why), so the copy
 * below never claims a send definitely happened; the masked `hint` is shown
 * when the server chose to reveal one.
 *
 * Saved studios (staff-person-login-s2b, 2026-09-27): the cards above the box
 * are this device's own record of who signed in here and which studios the
 * server listed for them (`lib/saved-logins.ts` — no token, no code). One tap
 * either OPENS the studio when this device still holds a session, or sends
 * the code to that person's allowlisted address / number exactly as typing
 * the identifier would; the verify lands directly in that studio. "Resend"
 * honours the server's 60 s floor with a countdown.
 *
 * The code box comes first (login-code-autofill, 2026-10-10). iOS 26 reads
 * the code out of the WhatsApp notification and offers it above the keyboard
 * of a box marked `autocomplete="one-time-code"`. Two things kept that from
 * this page, both about WHEN the box exists and takes the focus:
 *
 *   - An iPhone raises a keyboard for a script's `focus()` only inside the
 *     person's own tap or key press, or when a keyboard is already up. The
 *     page focused the box from a timer once the server had answered, so the
 *     code arrived with no keyboard on screen to offer it on.
 *   - An iPhone reads what a box is for once, as the box takes the focus.
 *     React recycled the identifier box into the code box, so a person who
 *     pressed Go kept a keyboard that still served "email or mobile number".
 *
 * So asking for a code builds the code step from new elements and focuses its
 * box INSIDE that tap or submit, before the request leaves the page: the
 * server sends the message before it answers. A code that then arrives whole
 * (that suggestion, or a paste) signs in without the button
 * (`lib/login-code.ts`). Whether iOS shows the suggestion is its own decision
 * and is only visible on a phone: `build-output/smoke-tests/staff-login-rails.md`.
 */

const STALE_MESSAGES: Record<string, string> = {
  // staff-session-slice-1 (2026-09-27): the server no longer knows this
  // session — most often "Sign out everywhere" was pressed on another device,
  // or the session expired. Nothing to repair; sign in again.
  whoami_unauthorized:
    "Your session is no longer valid on the server. It was signed out from another device, or it expired. Sign in again to continue.",
  studio_stale_session:
    "Your session referenced a studio that no longer exists in the database (most likely a db:reset since you signed in). Sign in again to get a fresh session bound to the current studio row.",
  studio_not_found:
    "Your session referenced a studio that no longer exists. Sign in again to get a fresh session.",
  // staff-person-login-studio-switcher (2026-09-27): no `staff_allowlist` row
  // anywhere names this address. The JWT proves the person, the allowlist
  // says which studios they may act in; nothing about the session itself is
  // wrong, and no metadata claim is involved any more.
  studio_unresolved:
    "No studio lists this address as staff. Ask an admin to add you on the studio's Config page, then sign in again.",
};

export function Login(): JSX.Element {
  const [params] = useSearchParams();
  const staleReason = params.get("reason") === "stale_session";
  const staleCode = params.get("code");
  const staleBanner = staleReason
    ? (STALE_MESSAGES[staleCode ?? ""] ?? STALE_MESSAGES["studio_stale_session"]!)
    : null;
  const staleTitle = STALE_TITLES[staleCode ?? ""] ?? "Stale session.";
  const [identifier, setIdentifier] = useState("");
  const [code, setCode] = useState("");
  // The code step: null until a code has been asked for. `pending` is that
  // request still in flight — the step shows from the tap, not from the answer.
  const [sent, setSent] = useState<{
    hint: string | null;
    pending: boolean;
  } | null>(null);
  const [msg, setMsg] = useState<{ kind: "ok" | "err"; text: string } | null>(
    null,
  );
  const [verifying, setVerifying] = useState(false);
  // The saved-studio cards, and whether THIS device still holds a session
  // (then a card opens the studio outright instead of sending a code).
  const [cards, setCards] = useState<SavedStudioCard[]>(() =>
    savedStudioCards(readSavedLogins(browserStorage())),
  );
  const [hasSession, setHasSession] = useState(false);
  // The studio a card asked for: where the verify lands.
  const [target, setTarget] = useState<SavedStudioCard | null>(null);
  const [resendAt, setResendAt] = useState<number | null>(null);
  const [now, setNow] = useState(() => Date.now());
  const identifierInput = useRef<HTMLInputElement | null>(null);
  const codeInput = useRef<HTMLInputElement | null>(null);
  // The cards' "Send code" buttons, by card: a request that fails hands the
  // focus back to the one that asked.
  const cardButtons = useRef(new Map<string, HTMLButtonElement>());
  // The newest code request. An answer to an older one, or to one the person
  // has stepped back from, is dropped.
  const requestSeq = useRef(0);
  // A verify in flight. A ref, because the suggestion and the button can both
  // fire before a state update lands.
  const verifyInFlight = useRef(false);

  useEffect(() => {
    let cancelled = false;
    void supabase.auth.getSession().then(({ data }) => {
      if (!cancelled) setHasSession(data.session !== null);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  // One-second tick while a resend countdown is showing.
  useEffect(() => {
    if (resendAt === null) return;
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [resendAt]);

  const resendInSeconds =
    resendAt === null ? 0 : Math.max(0, Math.ceil((resendAt - now) / 1000));

  /**
   * ONE request path for the form and the cards: the server's answer is
   * uniform by design, so the UI treats both the same way.
   *
   * Everything above the `await` runs inside the tap or submit that asked:
   * `flushSync` puts the code step in the DOM and its box is focused there,
   * so the keyboard is up, for a code, before the server has sent anything
   * (the header says why). A request that fails puts the page back on the
   * step it left, with the focus on what asked.
   */
  const requestCodeFor = async (
    id: string,
    card: SavedStudioCard | null,
  ): Promise<void> => {
    const before = { identifier, sent, target, resendAt };
    const seq = (requestSeq.current += 1);
    flushSync(() => {
      setMsg(null);
      setIdentifier(id);
      setTarget(card);
      // A new code supersedes the last one, so what is in the box is stale.
      setCode("");
      setResendAt(null);
      setSent({ hint: null, pending: true });
    });
    codeInput.current?.focus();
    const failed = (text: string): void => {
      if (seq !== requestSeq.current) return;
      flushSync(() => {
        setIdentifier(before.identifier);
        setSent(before.sent);
        setTarget(before.target);
        setResendAt(before.resendAt);
        setMsg({ kind: "err", text });
      });
      // The code box took the focus and is gone again, so the focus goes back
      // to what asked: the identifier box, or the card's own button. After a
      // failed resend neither is on the page, and the code box has kept it.
      if (card === null) identifierInput.current?.focus();
      else cardButtons.current.get(cardKey(card))?.focus();
    };
    try {
      const res = await fetch("/api/login-link", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ identifier: id }),
      });
      if (!res.ok) {
        failed("Sign-in is unavailable right now. Try again in a moment.");
        return;
      }
      const data = (await res.json()) as { ok: boolean; hint?: string | null };
      if (seq !== requestSeq.current) return;
      setSent({ hint: data.hint ?? null, pending: false });
      setResendAt(Date.now() + RESEND_AFTER_MS);
      setNow(Date.now());
    } catch {
      failed("Network error. Try again in a moment.");
    }
  };

  const requestLink = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    await requestCodeFor(identifier, null);
  };

  const openCard = (card: SavedStudioCard): void => {
    // A full load: the studio prefix is read once at boot (`main.tsx`).
    window.location.assign(studioPath(card.slug, "/"));
  };

  const forgetCard = (card: SavedStudioCard): void => {
    setCards(savedStudioCards(forgetSavedStudio(browserStorage(), card.identifier, card.slug)));
  };

  /**
   * Exchange a code for a session. The code is an argument, not read from
   * state, because a code that arrives whole is submitted from its own change
   * event, before `code` has caught up.
   */
  const verify = async (submitted: string): Promise<void> => {
    if (verifyInFlight.current) return;
    verifyInFlight.current = true;
    setVerifying(true);
    setMsg(null);
    try {
      const res = await fetch("/api/login-link/verify", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ identifier, code: submitted.trim() }),
      });
      const data = (await res.json()) as {
        ok: boolean;
        access_token?: string;
        refresh_token?: string;
      };
      if (!data.ok || !data.access_token || !data.refresh_token) {
        setMsg({
          kind: "err",
          text: "That code didn't work. Codes are 8 digits and expire after 1 hour — check for a newer message, or request a fresh one.",
        });
        return;
      }
      const { error } = await supabase.auth.setSession({
        access_token: data.access_token,
        refresh_token: data.refresh_token,
      });
      if (error) {
        setMsg({ kind: "err", text: "Could not start the session. Try again." });
        return;
      }
      // A FULL load, not a router navigation (staff-person-login-studio-
      // switcher, 2026-09-27): `main.tsx` derives the basename once at boot,
      // so re-entering the app must re-boot it. A card names its studio and
      // lands there; otherwise a bounce from `/s/clavis/members` to
      // `/s/clavis/login` lands back inside that studio, and a bare `/login`
      // lands on `/`, where the layout canonicalizes a single-studio person
      // or shows the chooser.
      window.location.assign(
        target !== null
          ? studioPath(target.slug, "/")
          : `${basenameFromPath(window.location.pathname)}/`,
      );
    } catch {
      setMsg({ kind: "err", text: "Network error. Try again in a moment." });
    } finally {
      verifyInFlight.current = false;
      setVerifying(false);
    }
  };

  return (
    <div className="login-card">
      <h1>Clavis · Dashboard</h1>
      {staleBanner && (
        <div
          className="msg"
          style={{
            color: "var(--c-warn-text-37)",
            background: "var(--c-warn-wash-93)",
            border: "1px solid var(--c-warn-line-86)",
            padding: "10px 12px",
            borderRadius: 4,
            marginBottom: 12,
          }}
        >
          <strong>{staleTitle}</strong> {staleBanner}
        </div>
      )}
      {/* Each step carries a key, so React builds it from new elements and
          never recycles the identifier box into the code box (the header says
          what that cost on an iPhone). */}
      {sent === null ? (
        <Fragment key="identifier">
          {cards.length > 0 && (
            <section className="login-saved" aria-label="Your studios">
              <h2 className="login-saved-title">Your studios</h2>
              <ul className="login-saved-list">
                {cards.map((card) => (
                  <li key={cardKey(card)} className="login-saved-card">
                    <div className="login-saved-text">
                      <span className="login-saved-name">{card.name}</span>
                      <span className="login-saved-id">{card.identifier}</span>
                    </div>
                    <div className="login-saved-actions">
                      {hasSession ? (
                        <button type="button" onClick={() => openCard(card)}>
                          Open
                        </button>
                      ) : (
                        <button
                          type="button"
                          ref={(el) => {
                            if (el) cardButtons.current.set(cardKey(card), el);
                            else cardButtons.current.delete(cardKey(card));
                          }}
                          onClick={() => void requestCodeFor(card.identifier, card)}
                        >
                          Send code
                        </button>
                      )}
                      <button
                        type="button"
                        className="linklike login-saved-forget"
                        aria-label={`Forget ${card.name} for ${card.identifier}`}
                        title="Forget this studio on this device"
                        onClick={() => forgetCard(card)}
                      >
                        ×
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
              <div className="login-saved-or">or sign in with another address</div>
            </section>
          )}
          <form onSubmit={(e) => void requestLink(e)}>
            <input
              ref={identifierInput}
              type="text"
              placeholder="email or mobile number"
              autoComplete="username"
              value={identifier}
              onChange={(e) => setIdentifier(e.target.value)}
              required
            />
            <button type="submit">Send sign-in link</button>
          </form>
        </Fragment>
      ) : (
        <Fragment key="code">
          {/* The box below is described by this text, so a screen reader reads
              it as the box takes the focus. The gap keeps the focused box's
              ring off the last line: the box is focused for as long as this
              step shows. */}
          <div
            id="login-code-help"
            className="msg"
            style={{ color: "var(--c-ok-text-39)", marginBottom: 12 }}
          >
            {sent.pending
              ? "Requesting your sign-in code…"
              : sent.hint !== null
                ? `Sent: ${sent.hint}.`
                : "If that matches a staff account, a sign-in link and code are on their way."}{" "}
            {target !== null
              ? `Enter the 8-digit code below to open ${target.name}.`
              : "Tap the emailed link, or enter the 8-digit code below."}{" "}
            {/* Said before the box is used: a field that submits by itself
                must not come as a surprise (WCAG 3.2.2, failure F36). */}
            A code you paste, or tap above your keyboard, signs you in at once.
          </div>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              void verify(code);
            }}
          >
            <input
              ref={codeInput}
              type="text"
              inputMode="numeric"
              placeholder="8-digit code"
              autoComplete="one-time-code"
              aria-describedby="login-code-help"
              value={code}
              onChange={(e) => {
                const next = e.target.value;
                setCode(next);
                if (arrivedWhole(code, next)) void verify(next);
              }}
              required
            />
            <button type="submit" disabled={verifying}>
              {verifying ? "Checking…" : "Sign in with code"}
            </button>
          </form>
          <div className="login-after">
            <button
              type="button"
              className="linklike"
              disabled={sent.pending || verifying || resendInSeconds > 0}
              onClick={() => void requestCodeFor(identifier, target)}
            >
              {resendInSeconds > 0 ? `Resend code in ${resendInSeconds}s` : "Resend code"}
            </button>
            <button
              type="button"
              className="linklike"
              onClick={() => {
                requestSeq.current += 1;
                setSent(null);
                setTarget(null);
                setResendAt(null);
                setCode("");
                setMsg(null);
              }}
            >
              Use a different email or number
            </button>
          </div>
        </Fragment>
      )}
      {msg && (
        <div
          className="msg"
          style={{ color: msg.kind === "err" ? "var(--c-danger-text-29)" : "var(--c-ok-text-39)" }}
        >
          {msg.text}
        </div>
      )}
    </div>
  );
}
